import React, { useState, useCallback } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Stack,
  Container,
  IconButton,
  Button,
  Box,
  Menu,
  MenuItem,
  TextField,
  InputAdornment,
  Badge,
} from "@mui/material";
import { Link as RouterLink, useNavigate, useLocation } from "react-router-dom";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import SearchIcon from "@mui/icons-material/Search";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import LoginIcon from "@mui/icons-material/Login";
import MenuIcon from "@mui/icons-material/Menu";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";

import LogoutConfirmModal from "../common/LogoutConfirm-Modal";
import { useUser } from "../../context/LoginDetail";
import { useModal } from "../../context/ModalContext";
import { useSettings } from "../../context/Settings";
import { assetUrl } from "../../utils/baseUrls";
export const navLinks = [
  { title: "Home", href: "/", basePath: "/" },
  { title: "Land", href: "/plot-marketplace", basePath: "/plot-marketplace" },
  { title: "Gioo Nano Plots", href: "/gioo-plots", basePath: "/gioo-plots" },
  { title: "Gio Rental Yield – Syndicate Plot", href: "/syndicate-plots", basePath: "/syndicate-plots" },
  { title: "Gio Rental Yield", href: "/rental-yield-plots", basePath: "/rental-yield-plots" },
  { title: "Flats / Villas", href: "/flats-villas", basePath: "/flats-villas" },
  { title: "Material Store", href: "/material-store", basePath: "/material-store" },
  { title: "Professional Services", href: "/professional-services", basePath: "/professional-services" },
];

export default function Header() {
  const { settings } = useSettings();
  const { setShowLogin } = useModal();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loadUser } = useUser();
  const isLoggedIn = !!user;

  const tickerText = settings?.header_ticker_text || "HOUSING  |  DEVELOPERS";
  const displayTicker = Array(8).fill(tickerText).join("        ");

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [logoutModalOpen, setLogoutModalOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  const logoUrl = settings?.data?.logo
    ? assetUrl(settings.data.logo)
    : "/assets/images/logo.png";

  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleDropdownOpen = (event, key) => {
    setActiveDropdown({ el: event.currentTarget, key });
  };
  const handleDropdownClose = () => setActiveDropdown(null);

  const handleSearchSubmit = (e) => {
    if (e.key === "Enter" && searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  const navMenuItems = [
    {
      title: "LAND",
      path: "/plot-marketplace",
      children: [
        { label: "All Land / Plots", path: "/plot-marketplace" },
        { label: "Featured Lands", path: "/plot-marketplace" },
      ],
    },
    {
      title: "GIO NANO PLOTS",
      path: "/gioo-plots",
      children: [
        { label: "All Nano Plots", path: "/gioo-plots" },
        { label: "Micro Investments", path: "/gioo-plots" },
      ],
    },
    {
      title: "SYNDICATE PLOT",
      path: "/syndicate-plots",
      children: [
        { label: "Co-Ownership Plots", path: "/syndicate-plots" },
        { label: "Syndicate Estates", path: "/syndicate-plots" },
      ],
    },
    {
      title: "FLATS / VILLAS",
      path: "/flats-villas",
      children: [
        { label: "Luxury Villas", path: "/flats-villas" },
        { label: "Modern Apartments", path: "/flats-villas" },
      ],
    },
    {
      title: "MATERIAL STORE",
      path: "/material-store",
      children: [
        { label: "Bricks & Blocks", path: "/material-store" },
        { label: "Cement & Binders", path: "/material-store" },
        { label: "Steel & TMT Bars", path: "/material-store" },
      ],
    },
  ];

  return (
    <Box sx={{ position: "sticky", top: 0, zIndex: 1100, bgcolor: "#FFFFFF" }}>
      {/* 1. Primary White Navigation Header */}
      <AppBar
        position="static"
        elevation={0}
        sx={{
          bgcolor: "#FFFFFF",
          color: "#1E3A23",
          borderBottom: "1px solid #EAEAEA",
          py: 0.5,
        }}
      >
        <Container maxWidth="xl">
          <Toolbar disableGutters sx={{ justifyContent: "space-between", minHeight: 70 }}>
            {/* Left: Brand Logo */}
            <RouterLink to="/" style={{ textDecoration: "none", display: "flex", alignItems: "center" }}>
              <Box
                component="img"
                src={logoUrl}
                alt="Greenheap Agro Farms"
                sx={{ height: { xs: 45, md: 58 }, width: "auto", objectFit: "contain" }}
              />
            </RouterLink>

            {/* Center: Desktop Menu Dropdowns */}
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              sx={{ display: { xs: "none", lg: "flex" } }}
            >
              {navMenuItems.map((item, idx) => (
                <Box key={idx}>
                  <Button
                    onClick={(e) => handleDropdownOpen(e, item.title)}
                    endIcon={<KeyboardArrowDownIcon sx={{ fontSize: "1.1rem !important" }} />}
                    sx={{
                      color: "#13381E",
                      fontWeight: 800,
                      fontSize: "0.85rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.4px",
                      px: 1.5,
                      py: 1,
                      fontFamily: "'Outfit', sans-serif",
                      "&:hover": { bgcolor: "transparent", color: "#59B400" },
                    }}
                  >
                    {item.title}
                  </Button>

                  <Menu
                    anchorEl={activeDropdown?.key === item.title ? activeDropdown.el : null}
                    open={activeDropdown?.key === item.title}
                    onClose={handleDropdownClose}
                    elevation={3}
                    sx={{ mt: 1 }}
                  >
                    {item.children.map((child, cIdx) => (
                      <MenuItem
                        key={cIdx}
                        onClick={() => {
                          navigate(child.path);
                          handleDropdownClose();
                        }}
                        sx={{ fontSize: "0.85rem", fontWeight: 700, color: "#13381E" }}
                      >
                        {child.label}
                      </MenuItem>
                    ))}
                  </Menu>
                </Box>
              ))}
            </Stack>

            {/* Right: Controls & Account */}
            <Stack direction="row" spacing={1.5} alignItems="center">
              {/* Pill Search Bar */}
              <Box sx={{ display: { xs: "none", sm: "block" }, width: { sm: 180, md: 220 } }}>
                <TextField
                  placeholder="SEARCH YOUR PROPERTY..."
                  variant="outlined"
                  size="small"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={handleSearchSubmit}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: "#163F1D", fontSize: "1.1rem" }} />
                      </InputAdornment>
                    ),
                    sx: {
                      bgcolor: "#EAF4EC",
                      borderRadius: "50px",
                      height: 38,
                      fontSize: "0.72rem",
                      fontWeight: 700,
                      "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                      "& input::placeholder": { color: "#163F1D", opacity: 0.75, fontWeight: 700 },
                    },
                  }}
                />
              </Box>

              {/* Notification Icon */}
              <Box
                sx={{
                  bgcolor: "#EAF4EC",
                  width: 38,
                  height: 38,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  position: "relative",
                }}
              >
                <Badge
                  variant="dot"
                  sx={{
                    "& .MuiBadge-badge": { bgcolor: "#FF5757", width: 8, height: 8, borderRadius: "50%" },
                  }}
                >
                  <NotificationsNoneIcon sx={{ color: "#163F1D", fontSize: "1.2rem" }} />
                </Badge>
              </Box>

              {/* Signup / Profile Button */}
              {!isLoggedIn ? (
                <Button
                  onClick={() => setShowLogin(true)}
                  startIcon={<LoginIcon sx={{ fontSize: "1.1rem !important" }} />}
                  sx={{
                    bgcolor: "#EAF4EC",
                    color: "#163F1D",
                    borderRadius: "50px",
                    px: 2.2,
                    py: 0.8,
                    fontWeight: 800,
                    fontSize: "0.82rem",
                    textTransform: "none",
                    boxShadow: "none",
                    "&:hover": { bgcolor: "#DDF0E1" },
                  }}
                >
                  Signup
                </Button>
              ) : (
                <Box
                  onClick={handleMenuOpen}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    bgcolor: "#EAF4EC",
                    borderRadius: "50px",
                    px: 1.8,
                    py: 0.5,
                    cursor: "pointer",
                  }}
                >
                  <Box
                    component="img"
                    src={user?.avatar || "/assets/images/profile.png"}
                    alt="user"
                    sx={{ width: 28, height: 28, borderRadius: "50%", objectFit: "cover" }}
                  />
                  <Typography sx={{ fontSize: "0.82rem", fontWeight: 800, color: "#163F1D" }}>
                    {user?.name?.split(" ")[0] || "Account"}
                  </Typography>
                </Box>
              )}

              {/* Mobile Drawer Icon */}
              <IconButton
                onClick={() => setDrawerOpen(true)}
                sx={{ display: { lg: "none" }, color: "#163F1D" }}
              >
                <MenuIcon />
              </IconButton>
            </Stack>
          </Toolbar>
        </Container>
      </AppBar>

      {/* 2. Sub-Header Edge-to-Edge Continuous Ticker Bar */}
      <Box
        sx={{
          width: "100%",
          bgcolor: "#163F1D",
          color: "#FFFFFF",
          py: 0.6,
          overflow: "hidden",
          whiteSpace: "nowrap",
          borderBottom: "1px solid #0F2F14",
        }}
      >
        <Box
          sx={{
            display: "inline-block",
            animation: "ticker 30s linear infinite",
            "@keyframes ticker": {
              "0%": { transform: "translateX(0%)" },
              "100%": { transform: "translateX(-50%)" },
            },
          }}
        >
          <Typography component="span" sx={{ fontWeight: 800, fontSize: "0.75rem", letterSpacing: "1.8px", mx: 3 }}>
            {displayTicker}
          </Typography>
          <Typography component="span" sx={{ fontWeight: 800, fontSize: "0.75rem", letterSpacing: "1.8px", mx: 3 }}>
            {displayTicker}
          </Typography>
        </Box>
      </Box>

      {/* User Profile Menu Dropdown */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem onClick={() => { handleMenuClose(); navigate("/dashboard"); }}>
          My Dashboard
        </MenuItem>
        <MenuItem onClick={() => { handleMenuClose(); setLogoutModalOpen(true); }}>
          Logout
        </MenuItem>
      </Menu>

      {/* Mobile Drawer */}
      <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <Box sx={{ width: 260, p: 2 }}>
          <List>
            {navMenuItems.map((item, idx) => (
              <ListItem key={idx} button onClick={() => { navigate(item.path); setDrawerOpen(false); }}>
                <ListItemText primary={item.title} primaryTypographyProps={{ fontWeight: 800, color: "#163F1D" }} />
              </ListItem>
            ))}
          </List>
        </Box>
      </Drawer>

      {/* Logout Modal */}
      <LogoutConfirmModal
        open={logoutModalOpen}
        onClose={() => setLogoutModalOpen(false)}
        onConfirm={() => {
          localStorage.removeItem("authToken");
          localStorage.removeItem("user");
          loadUser(null);
          setLogoutModalOpen(false);
          navigate("/");
        }}
      />
    </Box>
  );
}